/**
 * TanStack Router configuration
 *
 * This file creates and exports the router instance for the application.
 * It handles basepath configuration for PR deployments automatically.
 */

import { createRouter } from '@tanstack/react-router';
import { prDeployPath } from 'lib/config/appConstants';
import { routeTree } from '../../routeTree.gen';
import NotFound from 'components/NotFound/NotFound';

/**
 * Calculate the basepath for the router
 *
 * For PR deployments, paths look like: /edge/pr1234/pc
 * TanStack Router's basepath feature handles this natively by prepending
 * the basepath to all routes, eliminating the need for toRouterPath()
 *
 * @example
 * // When prDeployPath = '/pr1234' or 'pr1234'
 * // basepath = '/edge/pr1234'
 * // route '/pc' becomes '/edge/pr1234/pc'
 *
 * @example
 * // When prDeployPath = ''
 * // basepath = '/edge'
 * // route '/pc' becomes '/edge/pc'
 */
// Ensure proper slash handling - add leading slash if prDeployPath doesn't have one
const normalizedPrPath =
  prDeployPath && !prDeployPath.startsWith('/') ? `/${prDeployPath}` : prDeployPath;
const basepath = `/edge${normalizedPrPath}`;

/**
 * Router instance
 *
 * All route paths are automatically prefixed with the basepath.
 * This means:
 * - Route definitions use paths without /edge prefix (e.g., '/pc', '/eb')
 * - TanStack Router automatically prepends the basepath
 * - PR deployment paths work automatically
 */
export const router = createRouter({
  routeTree,
  basepath,
  defaultPreload: false,
  defaultStaleTime: 0, // Always refetch on navigation
  defaultGcTime: 0, // Don't keep cached data
  // Handle 404s
  defaultNotFoundComponent: NotFound,
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
